import { useEffect, useMemo, useState } from "react";
import audioPlayerManager from "@audio/AudioPlayerManager/AudioPlayerManager";
import { PrimaryButton } from "@common/Buttons";
import { BUTTON_SIZE } from "@constants/button";
import { AudioPlayerStateEnum, AudioType, IconEnum } from "@constants/consts";
import { useAudioEntry } from "@utils/audio/useAudioEntry";

const loadingStationText = "Zender laden...";

const StationButton: React.FC<{ slug: string }> = ({ slug }) => {
  const [state, setState] = useState<AudioPlayerStateEnum>(AudioPlayerStateEnum.STOP);

  const { data: station } = useAudioEntry<AudioStationEntry>({
    audioType: AudioType.STATION,
    slug,
  });

  useEffect(() => {
    if (!station) return;
    const onBeforePlay = () => {
      const stationLoaded = audioPlayerManager.media?.id === station.id;
      setState(stationLoaded ? AudioPlayerStateEnum.LOADING : AudioPlayerStateEnum.STOP);
    };

    const onStatusChange = () => {
      const stationLoaded = audioPlayerManager.media?.id === station.id;
      setState(
        stationLoaded && audioPlayerManager.getIsPlaying() ? AudioPlayerStateEnum.PLAYING : AudioPlayerStateEnum.STOP
      );
    };
    onStatusChange();

    audioPlayerManager.on("beforePlay", onBeforePlay);
    audioPlayerManager.on("play", onStatusChange);
    audioPlayerManager.on("pause", onStatusChange);
    audioPlayerManager.on("stop", onStatusChange);

    return () => {
      audioPlayerManager.off("beforePlay", onBeforePlay);
      audioPlayerManager.off("play", onStatusChange);
      audioPlayerManager.off("pause", onStatusChange);
      audioPlayerManager.off("stop", onStatusChange);
    };
  }, [station]);

  const { icon, handleClick } = useMemo(() => {
    if (state === AudioPlayerStateEnum.LOADING) {
      return { icon: IconEnum.SPINNER, handleClick: () => {} };
    }
    if (state === AudioPlayerStateEnum.PLAYING) {
      return { icon: IconEnum.STOP_FILLED, handleClick: () => audioPlayerManager.stop() };
    }
    return {
      icon: IconEnum.PLAY_ICON_FILLED,
      handleClick: () => {
        if (!station) return;
        audioPlayerManager.load(station);
        audioPlayerManager.start(station.mountSource);
      },
    };
  }, [state, station]);

  if (station === undefined)
    return (
      <PrimaryButton size={BUTTON_SIZE.MEDIUM} startIcon={IconEnum.SPINNER} onClick={() => {}} aria-disabled>
        {loadingStationText}
      </PrimaryButton>
    );
  if (!station) return null;

  return (
    <PrimaryButton
      size={BUTTON_SIZE.MEDIUM}
      startIcon={icon}
      onClick={handleClick}
      aria-disabled={state === AudioPlayerStateEnum.LOADING}
    >
      {`Luister ${station.title}`}
    </PrimaryButton>
  );
};

export default StationButton;
